import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Block, Button, PageContent, Sheet, f7, f7ready } from "framework7-react";
import { useBackClose } from "../../hooks/use-back-close";
import { vibrate } from "../../hooks/use-haptics";
import { isOnline } from "../../hooks/use-online";
import { useToast } from "../../hooks/use-toast";
import { noop } from "../../redux/api";
import { selectGroups } from "../../redux/reducer/app-slice";
import {
  loadNotificationSettings,
  saveNotificationSettings,
} from "../../redux/reducer/notifications";
import { onOwnEntryCreated } from "../../utils/own-entries";
import {
  isPromptShown,
  markPromptShown,
  shouldOfferPushPrompt,
} from "../../utils/push-prompt";
import { getPushRegistration } from "../../utils/push-subscription";
import { enablePush, refreshPushState } from "./push-state";

const OPEN_MODALS = ".popup.modal-in, .sheet-modal.modal-in, .dialog.modal-in";
const PROMPT_DELAY = 600;

const hasOpenModal = () => Boolean(document.querySelector(OPEN_MODALS));

const isDue = async () => {
  const push = await refreshPushState();
  const registration = await getPushRegistration().catch(() => null);
  return shouldOfferPushPrompt({
    support: push.support,
    permission: push.permission,
    subscribed: Boolean(push.endpoint),
    serverEnabled: Boolean(push.key?.enabled),
    hasWorker: Boolean(registration),
    online: isOnline(),
    alreadyShown: isPromptShown(),
  });
};

const useOwnEntryPrompt = (show) => {
  useEffect(() => {
    let candidate = null;
    let timer = null;
    let disposed = false;

    const tryShow = async () => {
      timer = null;
      if (candidate == null || disposed || hasOpenModal()) return;
      const groupId = candidate;
      const due = await isDue();
      candidate = null;
      if (!due || disposed || hasOpenModal()) return;
      markPromptShown();
      show(groupId);
    };
    const schedule = () => {
      if (candidate == null || timer) return;
      timer = setTimeout(tryShow, PROMPT_DELAY);
    };
    const stop = onOwnEntryCreated((groupId) => {
      if (groupId == null || isPromptShown()) return;
      candidate = groupId;
      schedule();
    });
    f7ready(() => {
      if (!disposed) f7.on("modalClosed", schedule);
    });
    return () => {
      disposed = true;
      stop();
      clearTimeout(timer);
      if (f7) f7.off("modalClosed", schedule);
    };
  }, [show]);
};

const NotificationPrompt = () => {
  const [prompt, setPrompt] = useState({ opened: false, groupId: null });
  const groups = useSelector(selectGroups);
  const dispatch = useDispatch();
  const { showError, showInfo } = useToast();
  const close = () => setPrompt((current) => ({ ...current, opened: false }));
  const releaseBack = useBackClose(prompt.opened, close);
  const show = useCallback(
    (groupId) => setPrompt({ opened: true, groupId }),
    [],
  );
  useOwnEntryPrompt(show);

  const groupName =
    groups.find((group) => `${group.id}` === `${prompt.groupId}`)?.name || "";

  const enableForGroup = (groupId) =>
    dispatch(
      loadNotificationSettings(
        groupId,
        (settings) => {
          if (!settings.enabled) {
            dispatch(
              saveNotificationSettings(groupId, { ...settings, enabled: true }),
            );
          }
        },
        noop,
      ),
    );

  const activate = async () => {
    const request = enablePush();
    const { groupId } = prompt;
    close();
    const result = await request;
    if (result.error) {
      showError(result.error);
      return;
    }
    if (!result.subscribed) return;
    enableForGroup(groupId);
    vibrate();
    showInfo(
      groupName
        ? `Benachrichtigungen für „${groupName}“ sind an.`
        : "Benachrichtigungen sind an.",
    );
  };

  return (
    <Sheet
      className="notification-prompt"
      style={{ height: "auto" }}
      opened={prompt.opened}
      onSheetClose={releaseBack}
      onSheetClosed={close}
      backdrop={false}
      closeByOutsideClick
      swipeToClose
    >
      <PageContent>
        <Block className="notification-prompt-text">
          <strong>
            {groupName
              ? `Benachrichtigungen für „${groupName}“ aktivieren?`
              : "Benachrichtigungen aktivieren?"}
          </strong>
          <div className="install-note">
            Du erfährst, wenn andere etwas eintragen.
          </div>
        </Block>
        <Block className="notification-prompt-actions">
          <Button tonal onClick={close}>
            Nicht jetzt
          </Button>
          <Button fill onClick={activate}>
            Aktivieren
          </Button>
        </Block>
      </PageContent>
    </Sheet>
  );
};

export default NotificationPrompt;
